import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { User, Mail, Phone, Briefcase, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  new: "Just joined",
  welcomed: "Welcomed",
  active: "Active member",
  nurtured: "Long-time friend",
};

export default function PublicProfilePage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    business_name: "",
    phone: "",
    unsubscribed: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/profile/${id}`)
      .then((r) => {
        if (r.status === 404) {
          setNotFound(true);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (!data) return;
        setProfile(data);
        setForm({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          business_name: data.business_name || "",
          phone: data.phone || "",
          unsubscribed: !!data.unsubscribed,
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/profile/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("save-failed");
      toast.success("Your profile has been updated.");
      setProfile((p) => ({ ...p, ...form }));
    } catch {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="editorial min-h-screen flex items-center justify-center" data-testid="profile-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[color:var(--ed-terracotta)]" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="editorial min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="profile-not-found">
        <h1 className="text-5xl">We couldn&apos;t find that profile.</h1>
        <p className="mt-4 font-body text-[color:var(--ed-muted)]">
          Please check the link, or reach out and we&apos;ll send you a new one.
        </p>
      </div>
    );
  }

  const displayName = form.first_name || profile.email || "friend";

  return (
    <div className="editorial min-h-screen" data-testid="public-profile-page">
      <Helmet>
        <title>Your profile – Artisan Market & Studio</title>
      </Helmet>

      <section className="max-w-3xl mx-auto px-6 pt-24 pb-8">
        <span className="kicker">Your Corner</span>
        <h1 className="mt-4 text-5xl md:text-6xl leading-[1.05]">
          Hello, {displayName}.
        </h1>
        <p className="mt-4 font-body text-lg text-[color:var(--ed-muted)] max-w-xl">
          This is your self-service profile. Update your details anytime, or let us know
          if you&apos;d rather step back from our list.
        </p>
        {profile.status && (
          <div className="mt-6 inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-[color:var(--ed-forest)]/15">
            <span className="w-2 h-2 rounded-full bg-[color:var(--ed-terracotta)]" />
            <span className="font-body text-sm text-[color:var(--ed-forest-dark)] font-semibold">
              {STATUS_LABELS[profile.status] || profile.status}
            </span>
          </div>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-32">
        <form onSubmit={save} className="card-warm space-y-6" data-testid="profile-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="First name" icon={User}>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="profile-input"
                data-testid="profile-first-name"
              />
            </Field>
            <Field label="Last name" icon={User}>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="profile-input"
                data-testid="profile-last-name"
              />
            </Field>
          </div>

          <Field label="Email (read-only)" icon={Mail}>
            <input
              type="email"
              value={profile.email || ""}
              disabled
              className="profile-input bg-[color:var(--ed-cream-warm)]/40 cursor-not-allowed"
              data-testid="profile-email"
            />
          </Field>

          <Field label="Craft / business" icon={Briefcase}>
            <input
              type="text"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="profile-input"
              placeholder="e.g. Willow Creek Pottery"
              data-testid="profile-business"
            />
          </Field>

          <Field label="Phone" icon={Phone}>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="profile-input"
              data-testid="profile-phone"
            />
          </Field>

          {Array.isArray(profile.tags) && profile.tags.length > 0 && (
            <div>
              <p className="kicker mb-2">Your interests</p>
              <div className="flex flex-wrap gap-2">
                {profile.tags.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full bg-[color:var(--ed-cream-warm)] border border-[color:var(--ed-forest)]/15 font-body text-sm">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          <label className="flex items-start gap-3 pt-4 border-t border-[color:var(--ed-forest)]/10">
            <input
              type="checkbox"
              checked={form.unsubscribed}
              onChange={(e) => setForm({ ...form, unsubscribed: e.target.checked })}
              className="mt-1 w-4 h-4 accent-[color:var(--ed-terracotta)]"
              data-testid="profile-unsubscribe"
            />
            <span className="font-body text-sm text-[color:var(--ed-muted)]">
              I&apos;d rather not receive newsletters from Artisan Market &amp; Studio.
            </span>
          </label>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary justify-center disabled:opacity-50"
            data-testid="profile-save-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" /> Save changes
              </>
            )}
          </button>
        </form>
      </section>

      <style>{`
        .profile-input {
          width: 100%;
          padding: 0.75rem 1rem;
          border-radius: 0.75rem;
          background: white;
          border: 1px solid rgba(47, 74, 58, 0.18);
          font-family: 'Manrope', sans-serif;
          transition: border-color 0.2s ease;
        }
        .profile-input:focus { outline: none; border-color: var(--ed-terracotta); }
      `}</style>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="kicker flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </span>
      {children}
    </label>
  );
}
